import React from 'react';
import { Input } from 'antd';
import { useIntl } from 'react-intl';
import SelectorLayout from '../../../shared/SelectorLayout';
import { RetentionOverrideRowState } from '../../types';
import styles from './RetentionOverrideRow.styles';

type Option = { label: string; value: string };

type Props = {
  row: RetentionOverrideRowState;
  index: number;
  statusOptions: Option[];
  blockReasonOptions: Option[];
  durationUnitOptions: Option[];
  onChange: (key: string, patch: Partial<RetentionOverrideRowState>) => void;
  onRemove: (key: string) => void;
};

const RetentionOverrideRow = ({
  row,
  index,
  statusOptions,
  blockReasonOptions,
  durationUnitOptions,
  onChange,
  onRemove,
}: Props) => {
  const intl = useIntl();

  const onChangeStatus = (value: any) =>
    onChange(row.key, {
      blocked: value === 'blocked',
      // Reasons only exist for blocked users — clear them in the same patch so
      // the invalid state is never representable.
      ...(value !== 'blocked' && { blockReasons: [] }),
    });

  const onChangeDurationNumber = (e) => {
    if (e.target.value >= 0)
      onChange(row.key, { durationNumber: e.target.value });
  };

  return (
    <>
      <div className="override-row">
        <div className="override-row-header">
          <span className="override-row-title">
            {`${intl.formatMessage({
              id: 'fleetadmin.purpose.retentionoverrides.rule',
            })} ${index + 1}`}
          </span>
          <img
            className="override-row-remove"
            src="/images/drawer/close.svg"
            alt="remove"
            title={intl.formatMessage({
              id: 'fleetadmin.purpose.retentionoverrides.remove',
            })}
            onClick={() => onRemove(row.key)}
          />
        </div>

        <div className="override-field override-duo">
          <div className="override-duo-col">
            <div className="label">
              {intl.formatMessage({
                id: 'fleetadmin.purpose.retentionoverrides.userstatus',
              })}
            </div>
            <SelectorLayout
              keyPrefix={`override-status-${row.key}`}
              placeholder={intl.formatMessage({
                id: 'fleetadmin.purpose.retentionoverrides.userstatus',
              })}
              options={statusOptions}
              value={row.blocked ? 'blocked' : 'active'}
              onSelect={onChangeStatus}
            />
          </div>

          <div className="override-duo-col">
            <div className="label">
              {intl.formatMessage({
                id: 'fleetadmin.purpose.retentionoverrides.blockreasons',
              })}
            </div>
            <SelectorLayout
              keyPrefix={`override-reasons-${row.key}`}
              mode="multiple"
              placeholder={intl.formatMessage({
                id: 'fleetadmin.purpose.retentionoverrides.blockreasons.all',
              })}
              options={blockReasonOptions}
              value={row.blockReasons}
              onSelect={(value: any) =>
                onChange(row.key, { blockReasons: value || [] })
              }
              disabled={!row.blocked}
              showSelectAll
            />
          </div>
        </div>

        <div className="override-field">
          <div className="label">
            {intl.formatMessage({ id: 'fleetadmin.purpose.maxduration' })}
          </div>
          <div className="row">
            <Input
              type="number"
              value={row.durationNumber}
              placeholder={intl.formatMessage({ id: 'number of units' })}
              onChange={onChangeDurationNumber}
              style={{ width: '50%' }}
              min={0}
            />
            <SelectorLayout
              keyPrefix={`override-unit-${row.key}`}
              placeholder="Select unit"
              options={durationUnitOptions}
              value={row.durationUnit}
              onSelect={(value) => onChange(row.key, { durationUnit: value })}
              style={{ width: '50%', marginLeft: 12 }}
            />
          </div>
        </div>
      </div>
      <style jsx>{styles}</style>
    </>
  );
};

export default RetentionOverrideRow;
